import type { CenaTabletop, CordaTabletop, VetorTabletop } from './tipos'

/**
 * A folga de cada trecho de uma corda: quantos metros de corda há a mais que a
 * distância reta entre os dois pontos. O trecho com folga fica frouxo e é
 * desenhado pendurado (a catenária de uma ancoragem, a folga de tensão de um
 * nó). A folga muda o desenho, o comprimento e as setas (trecho frouxo não
 * puxa); as contas de força que já existiam não a olham.
 *
 * A curva é uma parábola no plano que contém o trecho e a gravidade, com a
 * flecha escolhida para o comprimento dar `reta + folga`. Num trecho quase
 * vertical a gravidade não tem para onde puxar de lado, e a folga vira uma
 * barriga na horizontal (o laço que pende ao lado do nó). TypeScript puro.
 */

/** Folga máxima por trecho, em metros. */
export const FOLGA_MAXIMA_TRECHO_M = 5
/** Pontos por trecho frouxo, para desenhar e medir a curva. */
export const AMOSTRAS_TRECHO_FROUXO = 20

type V = VetorTabletop

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const esc = (a: V, k: number): V => [a[0] * k, a[1] * k, a[2] * k]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const prod = (a: V, b: V) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]

/** Para onde a barriga do trecho pende: a gravidade sem a parte ao longo do trecho, ou a horizontal num trecho vertical. */
function sentidoDaBarriga(u: V): V {
  const g: V = [0, -1, 0]
  const perp = sub(g, esc(u, prod(g, u)))
  const tamanho = comp(perp)
  // quanto mais vertical o trecho, mais a barriga vai para o lado (+X sem a parte ao longo do trecho)
  const lado = sub([1, 0, 0], esc(u, u[0]))
  const pesoLado = Math.max(0, 1 - tamanho / 0.25)
  const d = soma(perp, esc(lado, comp(lado) > 1e-9 ? pesoLado / comp(lado) : 0))
  const n = comp(d)
  return n > 1e-9 ? esc(d, 1 / n) : [0, -1, 0]
}

function curva(de: V, para: V, d: V, flecha: number, amostras: number): V[] {
  const corda = sub(para, de)
  return Array.from({ length: amostras + 1 }, (_, i) => {
    const t = i / amostras
    return soma(soma(de, esc(corda, t)), esc(d, flecha * 4 * t * (1 - t)))
  })
}

const comprimentoDaPolilinha = (pontos: readonly V[]) => {
  let total = 0
  for (let i = 1; i < pontos.length; i += 1) total += comp(sub(pontos[i]!, pontos[i - 1]!))
  return total
}

/**
 * Os pontos de um trecho, de `de` a `para`. Sem folga, são só as duas pontas;
 * com folga, a curva pendurada cujo comprimento é a distância reta mais a folga.
 */
export function pontosDoTrecho(de: V, para: V, folgaM: number, amostras = AMOSTRAS_TRECHO_FROUXO): V[] {
  const reta = comp(sub(para, de))
  if (!(folgaM > 1e-4) || reta < 1e-6) return [de, para]
  const alvo = reta + folgaM
  const d = sentidoDaBarriga(esc(sub(para, de), 1 / reta))
  // o comprimento cresce com a flecha: busca binária
  let baixo = 0
  let alto = alvo
  for (let k = 0; k < 50; k += 1) {
    const meio = (baixo + alto) / 2
    if (comprimentoDaPolilinha(curva(de, para, d, meio, amostras)) < alvo) baixo = meio
    else alto = meio
  }
  return curva(de, para, d, (baixo + alto) / 2, amostras)
}

/** A folga do trecho `i` (entre o ponto `i` e o `i + 1` do percurso); 0 se não foi definida. */
export const folgaDoTrecho = (corda: CordaTabletop, i: number): number => corda.folgas?.[i] ?? 0

/** A folga total da corda: o que ela tem a mais que os trechos retos. */
export const folgaTotalDaCorda = (corda: CordaTabletop): number => (corda.folgas ?? []).reduce((s, f) => s + f, 0)

/**
 * Define a folga de um trecho, de 0 a `FOLGA_MAXIMA_TRECHO_M`. Os zeros do fim
 * saem da lista, e sem folga nenhuma o campo sai da corda.
 */
export function definirFolgaDoTrecho(cena: CenaTabletop, idCorda: string, indice: number, folgaM: number): CenaTabletop {
  const corda = cena.cordas?.find((c) => c.id === idCorda)
  if (!corda || !Number.isInteger(indice) || indice < 0 || indice >= corda.percurso.length - 1) return cena
  const valor = Number.isFinite(folgaM) ? Math.min(FOLGA_MAXIMA_TRECHO_M, Math.max(0, folgaM)) : 0
  if (folgaDoTrecho(corda, indice) === valor) return cena
  const folgas = Array.from({ length: corda.percurso.length - 1 }, (_, i) => (i === indice ? valor : folgaDoTrecho(corda, i)))
  while (folgas.length > 0 && folgas[folgas.length - 1] === 0) folgas.pop()
  const { folgas: _antigas, ...semFolgas } = corda
  const nova: CordaTabletop = folgas.length > 0 ? { ...semFolgas, folgas } : semFolgas
  return { ...cena, cordas: cena.cordas!.map((c) => (c.id === idCorda ? nova : c)) }
}

/** Para a validação de uma cena importada. */
export const ehFolgasValidas = (folgas: unknown, pontosNoPercurso: number): boolean =>
  Array.isArray(folgas) &&
  folgas.length <= Math.max(0, pontosNoPercurso - 1) &&
  folgas.every((f) => typeof f === 'number' && Number.isFinite(f) && f >= 0 && f <= FOLGA_MAXIMA_TRECHO_M)
